import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { RefreshCw, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { ColorBadge, ALL_COLORS } from './colorLabels';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PersistenciaDashboard = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snapshots, setSnapshots] = useState([]);
  const [selectedSnapshot, setSelectedSnapshot] = useState('');
  const [freezing, setFreezing] = useState(false);

  const loadSnapshotsList = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/persistencia/snapshots`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await response.json();
      setSnapshots(d.snapshots || []);
    } catch (error) {
      console.error('Error cargando snapshots:', error);
    }
  }, [token]);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = selectedSnapshot ? `?snapshot_id=${selectedSnapshot}` : '';
      const response = await fetch(`${API_URL}/api/persistencia/dashboard${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await response.json();
      setData(d);
    } catch (error) {
      console.error('Error cargando dashboard de persistencia:', error);
      toast.error('Error cargando el dashboard');
    } finally {
      setLoading(false);
    }
  }, [token, selectedSnapshot]);

  useEffect(() => { loadSnapshotsList(); }, [loadSnapshotsList]);
  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const handleFreeze = async () => {
    setFreezing(true);
    try {
      const response = await fetch(`${API_URL}/api/persistencia/snapshots/freeze`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = await response.json();
      if (!response.ok) throw new Error(d.detail || 'Error congelando snapshot');
      toast.success(`Snapshot congelado: ${d.total_students} alumnos`);
      await loadSnapshotsList();
      setSelectedSnapshot(d.snapshot_id);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setFreezing(false);
    }
  };

  if (loading && !data) {
    return <p className="text-sm text-gray-400" data-testid="persistencia-dashboard-loading">Cargando semáforo...</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-semibold text-gray-900">Foto del Semáforo</h3>
          <p className="text-sm text-gray-500">
            {data?.mode === 'frozen' ? `Snapshot congelado del ${data.snapshot_date}` : 'Vista en vivo (sin congelar)'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            className="border rounded-md px-3 py-2 text-sm"
            value={selectedSnapshot}
            onChange={(e) => setSelectedSnapshot(e.target.value)}
            data-testid="persistencia-snapshot-selector"
          >
            <option value="">Vista en vivo</option>
            {snapshots.map((s) => (
              <option key={s.snapshot_id} value={s.snapshot_id}>
                {s.snapshot_date} {s.created_at ? new Date(s.created_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false }) : ''} ({s.total_students} alumnos)
              </option>
            ))}
          </select>
          <Button variant="outline" size="sm" onClick={loadDashboard} disabled={loading} data-testid="persistencia-dashboard-refresh-btn">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" onClick={handleFreeze} disabled={freezing} data-testid="persistencia-freeze-btn">
            <Camera className="h-4 w-4 mr-1" />
            {freezing ? 'Congelando...' : 'Congelar snapshot ahora'}
          </Button>
        </div>
      </div>

      {data && (
        <>
          <Card data-testid="persistencia-global-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Global ({data.total_students} alumnos)</CardTitle>
              <CardDescription>Todas las instancias combinadas</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                {ALL_COLORS.map((c) => (
                  <ColorBadge key={c} color={c} count={data.counts_global?.[c]} testId={`persistencia-global-color-${c}`} />
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="space-y-3" data-testid="persistencia-instances-list">
            {Object.entries(data.counts_by_instance || {}).map(([slug, inst]) => (
              <Card key={slug} data-testid={`persistencia-instance-card-${slug}`}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">{inst.name} ({inst.total} alumnos)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                    {ALL_COLORS.map((c) => (
                      <ColorBadge key={c} color={c} count={inst.counts?.[c]} testId={`persistencia-instance-${slug}-color-${c}`} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default PersistenciaDashboard;
